import React from 'react';
import { Box, Checkbox, Dialog, DialogContent, Divider, Grid, MenuItem, Typography } from '@mui/material';
import PatientDatas from 'src/modules/medcard/patient-datas';
import ByzMuiSaveButton from 'src/components/core-components/mui/buttons/ByzMuiSaveButton';
import ByzMuiTypography from 'src/components/core-components/mui/inputs/ByzMuiTypography';
import ByzIsolationSelectListPopupInput from '../modals/ByzIsolationSelectListPopupInput';
import ByzMuiTextField from 'src/components/core-components/mui/inputs/ByzMuiTextField';
import ByzMuiCloseButton from 'src/components/core-components/mui/buttons/ByzMuiCloseButton';
import { useTranslate } from 'src/locales';
import { IsolationReason, IsolationRow } from '../models';

type IsolationEditModalProps = {
  open: boolean;
  editRowData: IsolationRow | null;
  editRowIndex: number | null;
  editRowIsPending: boolean;
  onClose: () => void;
  onSave: (params: { editRowData: IsolationRow; editRowIndex: number | null; editRowIsPending: boolean }) => void;
  setEditRowData: React.Dispatch<React.SetStateAction<IsolationRow | null>>;
};

const IsolationEditModal: React.FC<IsolationEditModalProps> = ({
  open,
  editRowData,
  editRowIndex,
  editRowIsPending,
  onClose,
  onSave,
  setEditRowData,
}) => {
  const { t } = useTranslate();

  return (
    <Dialog
      open={open && !!editRowData}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: '90vh',
          width: '90vw',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      transitionDuration={0}
    >
      <DialogContent
        sx={{
          p: 0,
          overflow: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {editRowData && (
          <Box
            sx={{
              py: { xs: 2, sm: 3 },
              px: { xs: 1.5, sm: 2 },
              width: '100%',
              maxWidth: '100%',
            }}
          >
            {/* Başlık */}
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#f4f7fe',
                borderBottom: '1px solid #e0e0e0',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'flex-start',
                py: 1.5,
                px: 2.5,
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: 20,
                  lineHeight: '22px',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                {t('isolation-requests.change-isolation') || 'İzolasyon Değiştir'}
              </Typography>
            </Box>

            {/* Save / Close */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                gap: 1,
              }}
            >
              <ByzMuiSaveButton
                onClick={() => {
                  if (!editRowData) return;
                  onSave({ editRowData, editRowIndex, editRowIsPending });
                }}
              />
              <Box sx={{ flex: 1 }} />
              <ByzMuiCloseButton onClick={onClose} />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Hasta bilgisi kartı */}
            <Box sx={{ mb: 2 }}>
              <PatientDatas isCreateMedCard={true} isOpenMedCard={true} />
            </Box>

            {/* Form alanları */}
            <Box
              sx={{
                mt: 1,
                width: { xs: '100%', md: '60%' },
              }}
            >
              {/* Bölüm başlığı - İzolasyon Alınma Nedeni */}
              <Box
                sx={{
                  backgroundColor: '#F4F7FE',
                  borderBottom: '1px solid #E0E0E0',
                  mb: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                  }}
                >
                  {t('isolation-requests.form.isolation-take-reason')}
                </Typography>
              </Box>

              <Grid container spacing={0}>
                {/* İzolasyon Nedeni */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        pl: 8,
                        borderBottom: '1px solid #E0E0E0',
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1.5 }}>
                        {t('isolation-requests.form.isolation-reason')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <ByzIsolationSelectListPopupInput
                        value={editRowData.isolation_taken_reason_label || ''}
                        selectedId={editRowData.isolation_taken_reason}
                        onChange={((selectedRow: IsolationReason | null) => {
                          setEditRowData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  isolation_taken_reason: selectedRow ? selectedRow.id : null,
                                  isolation_taken_reason_label: selectedRow ? selectedRow.name : '',
                                }
                              : prev
                          );
                        }) as unknown as React.ChangeEventHandler<HTMLInputElement>}
                        url="isolation/api/IsolationTakenReason/GetAll"
                        method="GET"
                        label={t('isolation-requests.form.isolation-reason')}
                        dialogTitle={t('isolation-requests.form.isolation-reason')}
                        dialogShowOkButton={true}
                        onDialogOkClick={() => {}}
                        columns={[
                          { field: 'id', headerName: 'ID', width: 60 },
                          { field: 'name', headerName: 'Adı', flex: 2 },
                          { field: 'isolationtime', headerName: 'İzolasyon Süresi', flex: 2 },
                          { field: 'typename', headerName: 'İzolasyon Tipi', flex: 1.5 },
                        ]}
                        fullWidth
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Açıklama */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        pl: 8,
                        borderBottom: '1px solid #E0E0E0',
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1.5 }}>
                        {t('isolation-requests.form.description')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <ByzMuiTextField
                        label={t('isolation-requests.form.description')}
                        value={editRowData.isolation_taken_descrption || ''}
                        onChange={(e) =>
                          setEditRowData((prev) =>
                            prev ? { ...prev, isolation_taken_descrption: e.target.value } : prev
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            {/* Bakteri dışı izolasyon nedeni bölümü */}
            <Box
              sx={{
                mt: 3,
                width: { xs: '100%', md: '60%' },
              }}
            >
              {/* Bölüm başlığı */}
              <Box
                sx={{
                  backgroundColor: '#F4F7FE',
                  borderBottom: '1px solid #E0E0E0',
                  mb: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                  }}
                >
                  {t('isolation-requests.form.non-bacterial-isolation-reason')}
                </Typography>
              </Box>

              <Grid container spacing={0}>
                {/* Bakteri dışı izolasyon nedeni */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        pl: 8,
                        borderBottom: '1px solid #E0E0E0',
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1.5 }}>
                        {t('isolation-requests.form.isolation-reason')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <ByzIsolationSelectListPopupInput
                        value={editRowData.isolation_without_bacteria_label || ''}
                        selectedId={editRowData.isolation_without_bacteria}
                        onChange={((selectedRow: IsolationReason | null) => {
                          setEditRowData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  isolation_without_bacteria: selectedRow ? selectedRow.id : null,
                                  isolation_without_bacteria_label: selectedRow ? selectedRow.name : '',
                                }
                              : prev
                          );
                        }) as unknown as React.ChangeEventHandler<HTMLInputElement>}
                        url="isolation/api/IsolationTakenReasonWithoutBacteria/GetAll"
                        method="GET"
                        label={t('isolation-requests.form.non-bacterial-isolation-reason')}
                        dialogTitle={t('isolation-requests.form.non-bacterial-isolation-reason')}
                        dialogShowOkButton={true}
                        onDialogOkClick={() => {}}
                        columns={[
                          { field: 'id', headerName: 'ID', width: 60 },
                          { field: 'name', headerName: 'Adı', flex: 2 },
                          { field: 'isolationtime', headerName: 'İzolasyon Süresi', flex: 2 },
                          { field: 'typename', headerName: 'İzolasyon Tipi', flex: 1.5 },
                        ]}
                        fullWidth
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Bakteri dışı açıklama */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        pl: 8,
                        borderBottom: '1px solid #E0E0E0',
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1.5 }}>
                        {t('isolation-requests.form.description')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <ByzMuiTextField
                        label={t('isolation-requests.form.description')}
                        value={editRowData.isolation_without_bact_descrp || ''}
                        onChange={(e) =>
                          setEditRowData((prev) =>
                            prev ? { ...prev, isolation_without_bact_descrp: e.target.value } : prev
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            {/* Bildirimler bölümü */}
            <Box
              sx={{
                mt: 3,
                width: { xs: '100%', md: '60%' },
              }}
            >
              {/* Bölüm başlığı */}
              <Box
                sx={{
                  backgroundColor: '#F4F7FE',
                  borderBottom: '1px solid #E0E0E0',
                  mb: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                  }}
                >
                  {t('isolation-requests.form.isolation-notification')}
                </Typography>
              </Box>

              <Grid container spacing={0}>
                {/* Mikrobiyoloji */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        pl: 8,
                        borderBottom: '1px solid #E0E0E0',
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1.5 }}>
                        {t('isolation-requests.form.microbiology')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          size="small"
                          checked={editRowData.notification_lab === '1'}
                          onChange={(e) => {
                            const newValue = e.target.checked ? '1' : '0';
                            setEditRowData((prev) =>
                              prev ? { ...prev, notification_lab: newValue } : prev
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Mutfak */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        pl: 8,
                        borderBottom: '1px solid #E0E0E0',
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1.5 }}>
                        {t('isolation-requests.form.kitchen-team')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          size="small"
                          checked={editRowData.notification_kitchen === '1'}
                          onChange={(e) => {
                            const newValue = e.target.checked ? '1' : '0';
                            setEditRowData((prev) =>
                              prev ? { ...prev, notification_kitchen: newValue } : prev
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Temizlik */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        pl: 8,
                        borderBottom: '1px solid #E0E0E0',
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1.5 }}>
                        {t('isolation-requests.form.cleaning-team')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          size="small"
                          checked={editRowData.notification_cleaner === '1'}
                          onChange={(e) => {
                            const newValue = e.target.checked ? '1' : '0';
                            setEditRowData((prev) =>
                              prev ? { ...prev, notification_cleaner: newValue } : prev
                            );
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            {/* Hasta ve ailesi eğitimi */}
            <Box
              sx={{
                mt: 3,
                width: { xs: '100%', md: '60%' },
              }}
            >
              <Grid container spacing={0}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        pl: 8,
                        borderBottom: '1px solid #E0E0E0',
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1.5 }}>
                        {'Aileye Eğitim Verildi Mi ?'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <ByzMuiTextField
                        select
                        fullWidth
                        value={editRowData.is_given_family_education ?? ''}
                        onChange={(e) =>
                          setEditRowData((prev) =>
                            prev
                              ? { ...prev, is_given_family_education: e.target.value }
                              : prev
                          )
                        }
                      >
                        <MenuItem value="">
                        </MenuItem>
                        <MenuItem value="0">
                          {t('isolation-requests.common-no')}
                        </MenuItem>
                        <MenuItem value="1">
                          {t('isolation-requests.common-yes')}
                        </MenuItem>
                      </ByzMuiTextField>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IsolationEditModal;

